import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NavLink as NavLinkBase, useHistory, useLocation } from "react-router-dom";
import { IncreaseRequestCount, setPrevPath } from "@modules/global";
import { initParams } from "@modules/channels";
import { closeModal } from "@modules/createAssetsBulk";
import compareVersions, { VERSION_CM_5112_PROJECT_SITE_ACCESS } from "@cores/version";
import equal from "deep-equal";
import { openMenu, setMenu, toggleMenu } from "@modules/navigator";
import {
    IconArrowBack,
    IconArrowCircleRight,
    IconArrowForward,
    IconBack,
    IconChevronDown,
    IconChevronUp,
    IconCircle,
    IconSettingProject,
    IconTextureLibrary,
} from "@mzc-cloudplex/icons";
import SelectProject from "./components/SelectProject";
import { EllipsisListItemText } from "@components_v2/ellipsis-tooltip";
import menuService from "@services/menuService";
import {
    Box,
    Collapse,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListSubheader,
    Tooltip,
    Typography,
} from "@mzc-pdc/ui";
import { BackToConsoleButton, Drawer, ProjectSettingButton } from "./nav.styled";
import theme from "@styles/theme";
import { alpha, useMediaQuery } from "@mui/material";
import { NavigatorMenuSkeleton } from "@components/navigator/navigator-skeleton";
import useTextOverflow from "@hooks/useTextOverflow";
import { CUSTOM_MENU_DOMAIN } from "@constants";
import { LayoutForNoting } from "@components/layout";
import InvalidURL from "@routes/errors/InvalidURL";
import { siteMode } from "@cores/siteMode";

const NavLink = React.forwardRef((props, ref) => (
    <NavLinkBase
        ref={ref}
        {...props}
        className={({ isActive }) => `${props.className} ${isActive ? props.activeClassName : ""}`}
    />
));
NavLink.displayName = "NavLink";

const CustomNavigator = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const location = useLocation();
    const { t } = useTranslation();
    const isCms = siteMode.get() === "CMS";

    const [menuType, setMenuType] = useState("console");
    const { spaceId, projectId } = menuService.parse(location.pathname);
    const [navSwitch, setNavSwitch] = useState(false);
    const [activeStage, setActiveStage] = useState(null);
    const [activeProject, setActiveProject] = useState(null);
    const [navOpen, setNavOpen] = useState(true);

    const global = useSelector(({ global }) => global);
    const user = useSelector(({ user }) => user);
    const project = useSelector(({ project }) => project);
    const menus = useSelector(({ navigator }) => navigator.menus);
    const projectMenus = useSelector(({ navigator }) => navigator.projectMenus);
    const stage = useSelector(({ stage }) => stage);
    const customMenus = useSelector(({ customMenus }) => customMenus);

    const currentMenu = menus.find((item) => location.pathname.indexOf(item.path) !== -1);
    const upMatches = useMediaQuery(theme.breakpoints.up("xl"));

    // 하단 버튼 라벨은 조건부로 렌더링되므로 콜백 ref로 요소를 받아 마운트 시점에 잘림 여부를 측정한다.
    const [projectSettingsLabelEl, setProjectSettingsLabelEl] = useState(null);
    const [backToProjectLabelEl, setBackToProjectLabelEl] = useState(null);
    const projectSettingsLabel = t(`common::label::${"Project Settings"}`);
    const backToProjectLabel = t(`common::label::${"Back to Project"}`, `Back to Project`);
    const isProjectSettingsLabelClamped = useTextOverflow(projectSettingsLabelEl, [projectSettingsLabel]);
    const isBackToProjectLabelClamped = useTextOverflow(backToProjectLabelEl, [backToProjectLabel]);

    useEffect(() => {
        if (user && user.stages.data) {
            const activeStage = user.stages.data.filter((v) => v.id === stage.id)[0];
            if (activeStage) {
                const activeProject = activeStage.projects
                    ? activeStage.projects.filter((v) => v.id === project.id)[0]
                    : null;
                setActiveStage(activeStage);
                setActiveProject(activeProject);
            }
        }
    }, []);

    useEffect(() => {
        if (user && user.stages.data) {
            const activeStage = user.stages.data.filter((v) => v.id === stage.id)[0];
            if (activeStage) {
                const activeProject = activeStage.projects
                    ? activeStage.projects.filter((v) => v.id === project.id)[0]
                    : null;

                setActiveStage(activeStage);
                setActiveProject(activeProject);
            }
        }
    }, [user, project]);

    useEffect(() => {
        const newMenuType = location.pathname.split("/").filter((s) => s !== "")[0]; //ADMIN 페이지 여부

        if (!equal(menuType, newMenuType)) {
            setMenuType(newMenuType);
            dispatch(toggleMenu(menuType));
        }
    }, [navSwitch]);

    const navSwitchMenu = () => {
        setNavSwitch(!navSwitch);
    };

    const bindMenus = () => {
        const hideLive = stage && stage.extensions && stage.extensions["HIDE_LIVE"] === "true";
        const hideContents = stage && stage.extensions && stage.extensions["HIDE_CONTENTS"] === "true";

        const firstMenu = location.pathname.split("/")[1];
        if (!firstMenu) {
            history.push("/");
        }

        let menus = buildMenus({ hideLive, hideContents });

        dispatch(
            setMenu({
                menus: menus.map((v) => {
                    v.active = firstMenu ? v.path.indexOf(firstMenu) !== -1 : v.id === "transcoding";
                    return v;
                }),
                active: true,
            }),
        );
    };

    const buildMenus = ({ hideLive, hideContents }) => {
        const { spaceId, projectId } = menuService.parse(location.pathname);

        const excludedDomains = [];
        if (hideLive) {
            excludedDomains.push(CUSTOM_MENU_DOMAIN.STREAMS);
            excludedDomains.push(CUSTOM_MENU_DOMAIN.SCHEDULES);
            excludedDomains.push(CUSTOM_MENU_DOMAIN.CHANNELS);
        }
        if (hideContents) {
            excludedDomains.push(CUSTOM_MENU_DOMAIN.VIDEOS);
            excludedDomains.push(CUSTOM_MENU_DOMAIN.PEOPLE);
            excludedDomains.push(CUSTOM_MENU_DOMAIN.PHOTO);
            excludedDomains.push(CUSTOM_MENU_DOMAIN.MUSIC);
            excludedDomains.push(CUSTOM_MENU_DOMAIN.CUSTOM_CONTENTS);
            excludedDomains.push(CUSTOM_MENU_DOMAIN.JOBS);
        }

        const getFilterMenus = (menus, excludedDomains) => {
            return menus.filter((v) => !excludedDomains.includes(excludedDomains));
        };

        const getCustomMenuPath = (menu, spaceId, projectId, customMenuSetId) => {
            const menuId = menu.id;
            const domain = menu.domain;
            if (domain === CUSTOM_MENU_DOMAIN.COLLECTIONS && menu.type === "DETAIL") {
                const id = menu.searchParams.collections?.[0]?.id;
                if (isCms)
                    return menuService.paths.library.customMenuSets.customMenuDetail(
                        spaceId,
                        projectId,
                        customMenuSetId,
                        menuId,
                        id,
                    );
                else
                    return menuService.paths.customMenuSets.customMenuDetail(
                        spaceId,
                        projectId,
                        customMenuSetId,
                        menuId,
                        id,
                    );
            } else {
                if (isCms)
                    return menuService.paths.library.customMenuSets.customMenu(
                        spaceId,
                        projectId,
                        customMenuSetId,
                        menuId,
                    );
                else return menuService.paths.customMenuSets.customMenu(spaceId, projectId, customMenuSetId, menuId);
            }
        };

        const menus = getFilterMenus(customMenus.data, excludedDomains)?.map((el) => {
            let menu = {
                id: el.id,
                name: el.name,
                path: getCustomMenuPath(el, spaceId, projectId, customMenus.setId),
                icon: <IconTextureLibrary size={16} htmlColor={theme.palette.greySecondary.main} />,
                iconClass: "sprite sprite-menu-dashboard",
                disabled: false,
                visible: true,
                isOpen: true,
            };
            if (el.subMenus?.length > 0) {
                menu.subMenus = getFilterMenus(el.subMenus, excludedDomains).map((sub) => ({
                    id: sub.id,
                    name: sub.name,
                    path: getCustomMenuPath(sub, spaceId, projectId, customMenus.setId),
                    disabled: false,
                    visible: true,
                }));
            }
            return menu;
        });
        return menus;
    };

    const handleNavToggle = () => {
        setNavOpen(!navOpen);
    };

    const getNavToggleIcon = () => {
        if (navOpen) return <IconArrowBack size={12} htmlColor={`#fff`} />;
        return <IconArrowForward size={12} htmlColor={`#fff`} />;
    };

    const leave = () => {
        setNavSwitch(true);

        navSwitchMenu();
        history.replace("/");
    };

    const navMenus = useMemo(() => {
        return menus?.map((item) => {
            item.hasSubmenu = item?.subMenus?.length > 0;
            return item;
        });
    }, [menus]);

    useEffect(() => {
        setNavOpen(upMatches);
    }, [upMatches]);

    useEffect(() => {
        if (!customMenus.data) return;
        bindMenus();
    }, [customMenus.data]);

    if (customMenus.error) {
        return null;
    }
    return (
        <>
            <Drawer
                variant="permanent"
                open={navOpen}
                sx={{
                    backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                    ".MuiDrawer-paper": {
                        top: "56px",
                        height: `calc(100% - 56px )`,
                        overflowY: "hidden",
                        backgroundColor: "transparent",
                    },
                }}
            >
                <SelectProject open={navOpen} />
                {menuType === "admin" && (
                    <>
                        {navOpen ? (
                            <Box
                                sx={{
                                    margin: `16px 24px`,
                                }}
                            >
                                <Typography
                                    variant={"subtitle2_700"}
                                    sx={{
                                        // 고정 height 대신 minHeight로 두어, pt-BR 등 긴 라벨이
                                        // 2줄로 줄바꿈될 때 잘리지 않고 늘어나도록 한다.
                                        minHeight: `22px`,
                                        textTransform: `uppercase`,
                                        // Drawer가 whiteSpace: nowrap을 상속시켜 헤더가 줄바꿈되지 않고
                                        // margin을 넘어 흘러넘친다. normal로 상속을 끊어 2줄 줄바꿈되게 한다. (DAM-306)
                                        whiteSpace: `normal`,
                                        wordBreak: `break-word`,
                                    }}
                                >
                                    {t(`common::label::${"Project Settings"}`)}
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: `inline-flex`,
                                    alignItems: `center`,
                                    justifyContent: `center`,
                                    width: `40px`,
                                    height: `22px`,
                                    margin: `28px 8px 28px 12px`,
                                    backgroundColor: theme.palette.greySecondary.main,
                                    borderRadius: `4px`,
                                }}
                            >
                                <IconSettingProject size={16} htmlColor={`#fff`} />
                            </Box>
                        )}
                    </>
                )}
                {menuType === "admin" ? (
                    <>
                        <List>
                            {projectMenus &&
                                projectMenus.map((menu, i) => {
                                    let menuActive = location.pathname.includes(menu.path);
                                    return (
                                        <ListItem key={i} divider>
                                            {menu.path ? (
                                                <ListItemButton
                                                    selected={menuActive}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (location.pathname !== menu.path) {
                                                            // console.log("menu.path", menu.path);
                                                            history.push(menu.path);
                                                            dispatch(IncreaseRequestCount());
                                                            dispatch(toggleMenu(menu.id));
                                                            if (menu.path.indexOf("admin") !== -1) {
                                                                dispatch(initParams());
                                                                dispatch(
                                                                    setPrevPath(
                                                                        `${location.pathname}${
                                                                            location.search ? location.search : ""
                                                                        }`,
                                                                    ),
                                                                );
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon>{menu.icon && menu.icon}</ListItemIcon>

                                                    <EllipsisListItemText
                                                        primary={menu.name}
                                                        primaryTypographyProps={{
                                                            variant: "body1_500",
                                                            component: "strong",
                                                        }}
                                                    />
                                                </ListItemButton>
                                            ) : (
                                                <>
                                                    <ListSubheader component={"div"}>
                                                        {navOpen ? (
                                                            <Typography
                                                                variant={"body4_700"}
                                                                color={theme.palette.text.secondary}
                                                            >
                                                                {t(`${menu.name}`)}
                                                            </Typography>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: `8px`,
                                                                    height: `2px`,
                                                                    borderRadius: `1px`,
                                                                    margin: `7px auto`,
                                                                    backgroundColor: theme.palette.secondary[600],
                                                                }}
                                                            />
                                                        )}
                                                    </ListSubheader>
                                                    {menu.subMenus && (
                                                        <List>
                                                            {menu.subMenus
                                                                .filter((v) => !v.disabled)
                                                                .map((subMenu, i) => (
                                                                    <ListItem key={i}>
                                                                        <ListItemButton
                                                                            component={NavLink}
                                                                            selected={
                                                                                location.pathname === subMenu.path
                                                                            }
                                                                            to={{
                                                                                pathname: subMenu.path,
                                                                                search:
                                                                                    location.pathname === subMenu.path
                                                                                        ? location.search
                                                                                        : "",
                                                                            }}
                                                                            onClick={() => {
                                                                                dispatch(openMenu(menu.id));
                                                                                dispatch(IncreaseRequestCount());
                                                                            }}
                                                                        >
                                                                            <ListItemIcon>
                                                                                {subMenu.icon && subMenu.icon}
                                                                            </ListItemIcon>

                                                                            <EllipsisListItemText
                                                                                primary={subMenu.name}
                                                                                primaryTypographyProps={{
                                                                                    variant: "body1_500",
                                                                                    component: "strong",
                                                                                }}
                                                                            />
                                                                        </ListItemButton>
                                                                        {/*</NavLink>*/}
                                                                    </ListItem>
                                                                ))}
                                                        </List>
                                                    )}
                                                </>
                                            )}
                                        </ListItem>
                                    );
                                })}
                        </List>
                    </>
                ) : (
                    <>
                        {customMenus.pending ? (
                            <List sx={{ mt: theme.spacing(1) }}>
                                {new Array(5).fill("").map((_, i) => (
                                    <NavigatorMenuSkeleton key={i} />
                                ))}
                            </List>
                        ) : (
                            <List>
                                {navMenus &&
                                    navMenus.map((menu, i) => {
                                        let menuActive = location.pathname.includes(menu.path);
                                        if (menu.hasSubmenu === true && menuActive === false) {
                                            menuActive = !!menu.subMenus.find((sm) =>
                                                location.pathname.includes(sm.path),
                                            );
                                        }
                                        const menuHidden = false;
                                        return (
                                            <Tooltip key={i} title={navOpen ? "" : menu.name} placement={"right"}>
                                                <ListItem hidden={menuHidden}>
                                                    {menu.path ? (
                                                        <>
                                                            {menu.subMenus ? (
                                                                <ListItemButton
                                                                    onClick={(e) => {
                                                                        if (navOpen) dispatch(toggleMenu(menu.id));
                                                                        else {
                                                                            const firstSubMenu = menu.subMenus.filter(
                                                                                (v) => !v.disabled,
                                                                            )?.[0];
                                                                            if (!firstSubMenu) return;

                                                                            if (location.pathname !== menu.path) {
                                                                                dispatch(openMenu(firstSubMenu.id));
                                                                                dispatch(IncreaseRequestCount());
                                                                                history.push(firstSubMenu.path);
                                                                            }
                                                                        }
                                                                    }}
                                                                    selected={menuActive}
                                                                >
                                                                    {menu.icon && (
                                                                        <ListItemIcon>{menu.icon}</ListItemIcon>
                                                                    )}
                                                                    <EllipsisListItemText
                                                                        primary={menu.name}
                                                                        disableTooltip={!navOpen}
                                                                        primaryTypographyProps={{
                                                                            variant: "body1_500",
                                                                            component: "strong",
                                                                        }}
                                                                    />
                                                                    {navOpen && (
                                                                        <>
                                                                            {menu.isOpen ? (
                                                                                <IconChevronUp
                                                                                    size={20}
                                                                                    color={
                                                                                        menuActive
                                                                                            ? "primary"
                                                                                            : "secondary"
                                                                                    }
                                                                                    style={{
                                                                                        opacity: 0.48,
                                                                                        transition:
                                                                                            "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <IconChevronDown
                                                                                    size={20}
                                                                                    color={
                                                                                        menuActive
                                                                                            ? "primary"
                                                                                            : "secondary"
                                                                                    }
                                                                                    style={{
                                                                                        opacity: 0.48,
                                                                                        transition:
                                                                                            "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </ListItemButton>
                                                            ) : (
                                                                // <Link
                                                                //
                                                                // >
                                                                <ListItemButton
                                                                    selected={menuActive}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        // 현재 URL의 pathname과 이동하려는 menu.path가 같으면 이동하지 않음
                                                                        if (location.pathname !== menu.path) {
                                                                            history.push(menu.path);
                                                                            dispatch(IncreaseRequestCount());

                                                                            if (menu.path.indexOf("admin") !== -1) {
                                                                                dispatch(initParams());
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {menu.icon && (
                                                                        <ListItemIcon>{menu.icon}</ListItemIcon>
                                                                    )}
                                                                    <EllipsisListItemText
                                                                        primary={menu.name}
                                                                        disableTooltip={!navOpen}
                                                                        primaryTypographyProps={{
                                                                            variant: "body1_500",
                                                                            component: "strong",
                                                                        }}
                                                                    />
                                                                </ListItemButton>
                                                                // </Link>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <ListItemButton>
                                                            <EllipsisListItemText
                                                                primary={menu.name}
                                                                disableTooltip={!navOpen}
                                                                primaryTypographyProps={{
                                                                    variant: "body1_500",
                                                                    component: "strong",
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    )}
                                                    {menu.subMenus && navOpen && (
                                                        <Collapse in={menu.isOpen}>
                                                            <List>
                                                                {menu.subMenus
                                                                    .filter(
                                                                        (v) =>
                                                                            !v.disabled &&
                                                                            (v.visible === undefined || v.visible),
                                                                    )
                                                                    .map((subMenu, i) => (
                                                                        <ListItem key={i}>
                                                                            <ListItemButton
                                                                                component={NavLink}
                                                                                to={{
                                                                                    pathname: subMenu.path,
                                                                                    search:
                                                                                        location.pathname ===
                                                                                        subMenu.path
                                                                                            ? location.search
                                                                                            : "",
                                                                                    state: "flush",
                                                                                }}
                                                                                onClick={() => {
                                                                                    // console.log(
                                                                                    //     location.pathname ===
                                                                                    //         subMenu.path,
                                                                                    // );
                                                                                    dispatch(openMenu(menu.id));
                                                                                    dispatch(IncreaseRequestCount());
                                                                                }}
                                                                                activeClassName={"Mui-selected"}
                                                                                sx={{
                                                                                    "&.Mui-selected": {
                                                                                        "& .MuiListItemIcon-root": {
                                                                                            color: `${theme.palette.primary.main} !important`,
                                                                                        },
                                                                                        "& .MuiTypography-root": {
                                                                                            color: theme.palette.primary
                                                                                                .main,
                                                                                        },
                                                                                    },
                                                                                    "&:hover": {
                                                                                        backgroundColor: `${alpha(
                                                                                            theme.palette.primary[100],
                                                                                            0.72,
                                                                                        )} !important`,
                                                                                        "& .MuiListItemIcon-root": {
                                                                                            color: `${theme.palette.primary.main} !important`,
                                                                                        },
                                                                                        "& .MuiTypography-root": {
                                                                                            color: theme.palette.primary
                                                                                                .main,
                                                                                        },
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <ListItemIcon
                                                                                    sx={{
                                                                                        opacity: `1 !important`,
                                                                                        color: `${theme.palette.greySecondary.light} !important`,
                                                                                    }}
                                                                                >
                                                                                    <IconCircle size={6} />
                                                                                </ListItemIcon>
                                                                                <EllipsisListItemText
                                                                                    primary={subMenu.name}
                                                                                    primaryTypographyProps={{
                                                                                        variant: "body1_400",
                                                                                    }}
                                                                                />
                                                                            </ListItemButton>
                                                                            {/*</NavLink>*/}
                                                                        </ListItem>
                                                                    ))}
                                                            </List>
                                                        </Collapse>
                                                    )}
                                                </ListItem>
                                            </Tooltip>
                                        );
                                    })}
                            </List>
                        )}
                    </>
                )}

                {!isCms &&
                    activeStage &&
                    (compareVersions(activeStage.version, VERSION_CM_5112_PROJECT_SITE_ACCESS) < 0 ||
                        (activeStage.user &&
                            (activeStage.user.isSpaceAdmin ||
                                (activeProject &&
                                    activeStage.user.projects &&
                                    activeStage.user.projects.filter((p) => p.id === activeProject.id && p.isAdmin)
                                        .length > 0)))) && (
                        <Box sx={{ position: `relative`, minHeight: "64px", bottom: "0", backgroundColor: `#f2f3f6` }}>
                            <Divider sx={{ mb: 1.5, mx: 1.5 }} />
                            {menuType === "admin" ? (
                                <Tooltip
                                    title={!navOpen || isBackToProjectLabelClamped ? backToProjectLabel : ""}
                                    placement={"right"}
                                >
                                    <BackToConsoleButton
                                        color="inherit"
                                        className={"back-to-console-button"}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            leave();
                                        }}
                                        startIcon={
                                            <Box width={22} height={22} borderRadius={5}>
                                                <IconBack size={12} htmlColor={`#fff`} />
                                            </Box>
                                        }
                                    >
                                        <span className={"nav-button-label"} ref={setBackToProjectLabelEl}>
                                            {backToProjectLabel}
                                        </span>
                                    </BackToConsoleButton>
                                </Tooltip>
                            ) : (
                                <>
                                    <Tooltip
                                        title={!navOpen || isProjectSettingsLabelClamped ? projectSettingsLabel : ""}
                                        placement={"right"}
                                    >
                                        <ProjectSettingButton
                                            color="inherit"
                                            className={"project-setting-button"}
                                            onClick={() => {
                                                dispatch(closeModal());
                                                navSwitchMenu();
                                                project.id
                                                    ? history.push(
                                                          menuService.paths.admin.projectSettings(spaceId, projectId),
                                                      )
                                                    : "";
                                            }}
                                            startIcon={
                                                <IconSettingProject
                                                    size={16}
                                                    htmlColor={theme.palette.greySecondary.main}
                                                />
                                            }
                                            endIcon={
                                                <Box borderRadius={5}>
                                                    <IconArrowCircleRight size={22} />
                                                </Box>
                                            }
                                        >
                                            <span className={"nav-button-label"} ref={setProjectSettingsLabelEl}>
                                                {projectSettingsLabel}
                                            </span>
                                        </ProjectSettingButton>
                                    </Tooltip>
                                </>
                            )}
                        </Box>
                    )}
                <Tooltip
                    title={
                        navOpen ? t(`common::label::${"Collapse"}`, `Collapse`) : t(`common::label::${"Open"}`, `Open`)
                    }
                    placement={"right"}
                    leaveDelay={-200}
                >
                    <IconButton
                        className={"nav-toggle-button"}
                        color={navOpen ? "primary" : "inherit"}
                        variant={"contained"}
                        circled
                        onClick={handleNavToggle}
                        edge="start"
                    >
                        {getNavToggleIcon()}
                    </IconButton>
                </Tooltip>
            </Drawer>
        </>
    );
};

export default CustomNavigator;
